import { CurrencyPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

import * as echarts from 'echarts';

import { TvoedChartData, ChartDataPoint } from '../../models/tvoed-chart-data.model';
import { TvoedComputedExtraPayment } from '../../models/tvoed-extra-payment.model';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

@Component({
  selector: 'tvoed-rechner-monthly-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './monthly-chart.component.html',
  styleUrls: ['./monthly-chart.component.scss'],
  providers: [CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonthlyChartComponent implements AfterViewInit {
  @ViewChild('echarts') echarts!: ElementRef;

  private currencyPipe = inject(CurrencyPipe);

  @Input() set data(data: TvoedChartData) {
    if (data) {
      const monthlyBaseSalary: ChartDataPoint[] = [];
      const extraHoursSalary: ChartDataPoint[] = [];
      const zulagen: ChartDataPoint[] = [];
      const extraPayments: ChartDataPoint[] = [];
      let extraHoursSalaryName = 'Überstundenzuschlag';

      if (data.extraHoursSalary < 0) {
        extraHoursSalaryName = 'Minderstundenabzug';
      }

      MONTHS.forEach(month => {
        monthlyBaseSalary.push({ month, value: data.monthlyBaseSalary, name: 'Monatliches Grundgehalt' });
        extraHoursSalary.push({ month, value: data.extraHoursSalary, name: extraHoursSalaryName });
        zulagen.push({ month, value: data.zulagenTotal ?? 0, name: 'Zulagen' });

        const extraPayment = data.extraPayments
          .filter((ep: TvoedComputedExtraPayment) => ep.paymentMonth === month)
          .reduce(
            (acc: { money: number; name: string }, ep: TvoedComputedExtraPayment) => {
              return { money: (acc.money += ep.money), name: (acc.name += `${ep.name}, `) };
            },
            { money: 0, name: '' }
          );
        if (extraPayment) {
          extraPayments.push({ month, value: extraPayment.money, name: extraPayment.name.substring(0, extraPayment.name.length - 2) });
        } else {
          extraPayments.push({ month, value: 0 });
        }
      });

      if (this.chartInstance) {
        this.chartInstance.setOption({
          dataset: [
            { dimensions: ['month', 'value', 'name'], source: monthlyBaseSalary },
            { dimensions: ['month', 'value', 'name'], source: extraHoursSalary },
            { dimensions: ['month', 'value', 'name'], source: zulagen },
            { dimensions: ['month', 'value', 'name'], source: extraPayments }
          ]
        });
      } else {
        this.option.dataset = [
          { dimensions: ['month', 'value', 'name'], source: monthlyBaseSalary },
          { dimensions: ['month', 'value', 'name'], source: extraHoursSalary },
          { dimensions: ['month', 'value', 'name'], source: zulagen },
          { dimensions: ['month', 'value', 'name'], source: extraPayments }
        ];
      }
    }
  }

  chartInstance!: echarts.ECharts;
  option: echarts.EChartsOption = {
    legend: {},
    tooltip: {
      confine: true,
      formatter: params => {
        if (Array.isArray(params)) return '';
        const data = params.data as unknown as ChartDataPoint;
        const marker = params.marker as string;
        let message = '';
        message += `${data.month}`;
        message += `<br/>${marker}<b>${data.name}</b>: ${this.currencyPipe.transform(data.value, 'EUR')}`;
        return message;
      }
    },
    color: ['#4A7FB5', '#6BA3D6', '#81B8E8', '#A0CFF2', '#79B7E0', '#3A6F9F'],
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          show: true,
          interval: 0,
          rotate: 45
        },
        data: MONTHS
      }
    ],
    yAxis: {},
    grid: {
      containLabel: true,
      bottom: 0
    },
    series: [
      {
        type: 'bar',
        name: 'Monatliches Grundgehalt',
        stack: 'money',
        encode: { x: 'month', y: 'value' },
        color: '#4A7FB5',
        datasetIndex: 0
      },
      {
        type: 'bar',
        name: 'Überstundenzuschlag',
        stack: 'money',
        encode: { x: 'month', y: 'value' },
        color: '#6BA3D6',
        datasetIndex: 1
      },
      {
        type: 'bar',
        name: 'Zulagen',
        stack: 'money',
        encode: { x: 'month', y: 'value' },
        color: '#81B8E8',
        datasetIndex: 2
      },
      {
        type: 'bar',
        name: 'Zusatzgeld',
        stack: 'money',
        encode: { x: 'month', y: 'value' },
        color: '#A0CFF2',
        datasetIndex: 3
      }
    ]
  };
  ngAfterViewInit() {
    this.chartInstance = echarts.init(this.echarts.nativeElement);
    this.chartInstance.setOption(this.option);

    const resizeObserver = new ResizeObserver(() => {
      this.chartInstance.resize({
        animation: {
          duration: 500,
          easing: 'cubicIn'
        }
      });
    });

    resizeObserver.observe(this.echarts.nativeElement);
  }
}
